/**
 * Layout Initialization
 *
 * Handles sidebar toggle, accordions, theme switching, and navigation state.
 */

/**
 * Initialize all layout components
 */
function initLayout() {
  initSidebar()
  initAccordions()
  initGen2Navigation()
  initThemeToggle()
  autoExpandAccordions()
}

/**
 * Initialize sidebar toggle functionality
 */
function initSidebar() {
  const sidebar = document.getElementById('sidebar')
  const backdrop = document.getElementById('sidebar-backdrop')
  const toggleBtn = document.getElementById('sidebar-toggle')
  const closeBtn = document.getElementById('sidebar-close')
  const mainContent = document.getElementById('main-content')

  if (!sidebar || !toggleBtn) return

  // State for desktop sidebar collapse
  let isCollapsed = false

  function openMobileSidebar() {
    sidebar.classList.remove('-translate-x-full')
    sidebar.classList.add('translate-x-0')
    backdrop?.classList.remove('hidden')
    document.body.style.overflow = 'hidden'
  }

  function closeMobileSidebar() {
    sidebar.classList.add('-translate-x-full')
    sidebar.classList.remove('translate-x-0')
    backdrop?.classList.add('hidden')
    document.body.style.overflow = ''
  }

  function toggleDesktopSidebar() {
    isCollapsed = !isCollapsed
    if (isCollapsed) {
      sidebar.classList.add('-translate-x-full')
      sidebar.classList.remove('lg:translate-x-0')
      mainContent?.classList.remove('lg:ps-64')
    } else {
      sidebar.classList.remove('-translate-x-full')
      sidebar.classList.add('lg:translate-x-0')
      mainContent?.classList.add('lg:ps-64')
    }
  }

  function isDesktop() {
    return window.innerWidth >= 1024
  }

  toggleBtn.addEventListener('click', () => {
    if (isDesktop()) {
      toggleDesktopSidebar()
    } else {
      openMobileSidebar()
    }
  })

  closeBtn?.addEventListener('click', closeMobileSidebar)
  backdrop?.addEventListener('click', closeMobileSidebar)

  // Close mobile sidebar on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !isDesktop()) {
      closeMobileSidebar()
    }
  })

  // Handle resize: reset sidebar state when switching between mobile/desktop
  let wasDesktop = isDesktop()

  window.addEventListener('resize', () => {
    const nowDesktop = isDesktop()

    // Switching to desktop
    if (nowDesktop && !wasDesktop) {
      backdrop?.classList.add('hidden')
      document.body.style.overflow = ''
      if (!isCollapsed) {
        sidebar.classList.remove('-translate-x-full')
        sidebar.classList.add('lg:translate-x-0')
      }
    }

    // Switching to mobile - automatically hide sidebar
    if (!nowDesktop && wasDesktop) {
      closeMobileSidebar()
      // Reset desktop collapse state
      if (isCollapsed) {
        isCollapsed = false
        sidebar.classList.add('lg:translate-x-0')
        mainContent?.classList.add('lg:ps-64')
      }
    }

    wasDesktop = nowDesktop
  })
}

/**
 * Initialize accordion functionality
 */
function initAccordions() {
  const toggles = document.querySelectorAll('.accordion-toggle')

  toggles.forEach(toggle => {
    if (toggle.dataset.accordionInitialized === 'true') return
    toggle.dataset.accordionInitialized = 'true'

    toggle.addEventListener('click', () => {
      const accordionId = toggle.dataset.accordion
      const content = document.getElementById(`accordion-${accordionId}`)
      const icon = toggle.querySelector('.accordion-icon')

      if (!content) return

      const isOpen = !content.classList.contains('hidden')

      if (isOpen) {
        content.classList.add('hidden')
        icon?.classList.remove('rotate-180')
      } else {
        content.classList.remove('hidden')
        icon?.classList.add('rotate-180')
      }
    })
  })
}

const GEN2_OPEN_TOPICS_KEY = 'gen2.openTopics.v1'
const GEN2_OPEN_CHAPTERS_KEY = 'gen2.openChapters.v1'

function initGen2Navigation() {
  bindGen2HandlersOnce()
  restoreGen2StateFromUrlAndStorage()
  syncGen2Highlights()
}

function bindGen2HandlersOnce() {
  if (window.__gen2HandlersBound) return
  window.__gen2HandlersBound = true

  document.addEventListener('click', (event) => {
    const topicLink = event.target.closest('[data-gen2-topic-link="true"]')
    if (topicLink) {
      if (window.location.pathname !== '/chapters-gen-2') return
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return

      event.preventDefault()
      toggleGen2TopicFromLink(topicLink)
      return
    }

    const chapterToggle = event.target.closest('.accordion-toggle[data-gen2-chapter]')
    if (chapterToggle) {
      setTimeout(() => {
        const chapterKey = chapterToggle.dataset.gen2ChapterKey
        const isOpen = isAccordionOpen(chapterToggle.dataset.accordion)

        // Sync all accordions with the same chapter key to the same state
        if (chapterKey) {
          setGen2ChapterOpen(chapterKey, isOpen)
        }

        if (window.location.pathname === '/chapters-gen-2' && chapterKey && !isOpen) {
          closeGen2TopicsForChapter(chapterKey)
        }
        persistGen2OpenChapters()
        syncGen2Highlights()
      }, 0)
    }
  })
}

function closeGen2TopicsForChapter(chapterKey) {
  const openTopics = new Set(getStoredArray(GEN2_OPEN_TOPICS_KEY))
  const prefix = `${chapterKey}/`
  let changed = false

  openTopics.forEach((key) => {
    if (key && key.startsWith(prefix)) {
      openTopics.delete(key)
      changed = true
    }
  })

  document.querySelectorAll(`[id^="gen2-panel-${cssEscape(chapterKey)}-"]`).forEach((panel) => {
    panel.classList.add('hidden')
  })

  if (changed) {
    setStoredArray(GEN2_OPEN_TOPICS_KEY, Array.from(openTopics))
    updateGen2OpenTopicsUrl(openTopics)
  }
}

function restoreGen2StateFromUrlAndStorage() {
  const urlOpenTopics = gen2OpenTopicsFromUrl()
  if (urlOpenTopics) {
    setStoredArray(GEN2_OPEN_TOPICS_KEY, urlOpenTopics)
  }

  const openTopics = new Set(getStoredArray(GEN2_OPEN_TOPICS_KEY))
  const openChapters = new Set(getStoredArray(GEN2_OPEN_CHAPTERS_KEY))

  openChapters.forEach((accordionId) => {
    openGen2Chapter(accordionId)
  })

  if (window.location.pathname === '/chapters-gen-2') {
    openTopics.forEach((topicKey) => {
      openGen2Topic(topicKey)
    })
  }

  if (urlOpenTopics) updateGen2OpenTopicsUrl(new Set(urlOpenTopics))
}

function gen2OpenTopicsFromUrl() {
  if (window.location.pathname !== '/chapters-gen-2') return null

  const params = new URLSearchParams(window.location.search)
  const open = params.get('open')
  if (!open) return null

  return open
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

function toggleGen2TopicFromLink(linkEl) {
  const topicKey = linkEl.dataset.gen2Topic
  const chapter = linkEl.dataset.gen2Chapter
  const chapterKey = chapter
  const panelId = linkEl.dataset.gen2PanelId
  const frameId = linkEl.dataset.gen2FrameId
  const frameSrc = linkEl.dataset.gen2FrameSrc || linkEl.href

  if (!topicKey || !panelId) return

  const panel = document.getElementById(panelId)
  if (!panel) return

  const openTopics = new Set(getStoredArray(GEN2_OPEN_TOPICS_KEY))

  const isOpen = !panel.classList.contains('hidden')
  if (isOpen) {
    panel.classList.add('hidden')
    openTopics.delete(topicKey)
  } else {
    openGen2Chapter(chapterKey)
    panel.classList.remove('hidden')
    openTopics.add(topicKey)

    const frame = frameId ? document.getElementById(frameId) : null
    if (frame && frameSrc) {
      const currentSrc = frame.getAttribute('src')
      if (!currentSrc || currentSrc !== frameSrc) frame.setAttribute('src', frameSrc)
    }
  }

  setStoredArray(GEN2_OPEN_TOPICS_KEY, Array.from(openTopics))
  persistGen2OpenChapters()
  updateGen2OpenTopicsUrl(openTopics)
  syncGen2Highlights()
}

function openGen2Topic(topicKey) {
  const linkEl = document.querySelector(`[data-gen2-topic-link="true"][data-gen2-topic="${cssEscape(topicKey)}"]`)
  if (!linkEl) return

  const panelId = linkEl.dataset.gen2PanelId
  const frameId = linkEl.dataset.gen2FrameId
  const chapter = linkEl.dataset.gen2Chapter
  const frameSrc = linkEl.dataset.gen2FrameSrc || linkEl.href
  if (chapter) openGen2Chapter(chapter)

  const panel = panelId ? document.getElementById(panelId) : null
  if (panel) panel.classList.remove('hidden')

  const frame = frameId ? document.getElementById(frameId) : null
  if (frame && frameSrc) {
    const currentSrc = frame.getAttribute('src')
    if (!currentSrc) frame.setAttribute('src', frameSrc)
  }
}

function persistGen2OpenChapters() {
  const chapterKeys = new Set()
  document.querySelectorAll('.accordion-toggle[data-gen2-chapter-key]').forEach((toggle) => {
    const key = toggle.dataset.gen2ChapterKey
    if (!key) return
    if (isAccordionOpen(toggle.dataset.accordion)) chapterKeys.add(key)
  })

  setStoredArray(GEN2_OPEN_CHAPTERS_KEY, Array.from(chapterKeys))
}

function openGen2Chapter(chapterKey) {
  if (!chapterKey) return
  setGen2ChapterOpen(chapterKey, true)
}

function setGen2ChapterOpen(chapterKey, open) {
  document.querySelectorAll(`.accordion-toggle[data-gen2-chapter-key="${cssEscape(chapterKey)}"]`).forEach((toggle) => {
    setAccordionOpen(toggle.dataset.accordion, open)
  })
}

function setAccordionOpen(accordionId, open) {
  if (!accordionId) return

  const content = document.getElementById(`accordion-${accordionId}`)
  const toggle = document.querySelector(`.accordion-toggle[data-accordion="${cssEscape(accordionId)}"]`)
  const icon = toggle?.querySelector('.accordion-icon')

  if (!content) return

  if (open) {
    content.classList.remove('hidden')
    icon?.classList.add('rotate-180')
  } else {
    content.classList.add('hidden')
    icon?.classList.remove('rotate-180')
  }
}

function isAccordionOpen(accordionId) {
  if (!accordionId) return false
  const content = document.getElementById(`accordion-${accordionId}`)
  if (!content) return false
  return !content.classList.contains('hidden')
}

function syncGen2Highlights() {
  const openTopics = new Set(getStoredArray(GEN2_OPEN_TOPICS_KEY))

  document.querySelectorAll('[data-gen2-topic-link="true"]').forEach((el) => {
    const key = el.dataset.gen2Topic
    const isOpen = key && openTopics.has(key)

    el.classList.toggle('bg-indigo-50', isOpen)
    el.classList.toggle('dark:bg-indigo-900/20', isOpen)
    el.classList.toggle('text-indigo-600', isOpen)
    el.classList.toggle('dark:text-indigo-400', isOpen)

    if (isOpen) {
      el.classList.remove('text-gray-600', 'dark:text-gray-300')
    } else {
      el.classList.add('text-gray-600', 'dark:text-gray-300')
    }
  })
}

function updateGen2OpenTopicsUrl(openTopics) {
  if (window.location.pathname !== '/chapters-gen-2') return

  const params = new URLSearchParams(window.location.search)
  const open = Array.from(openTopics).sort().join(',')
  if (open) {
    params.set('open', open)
  } else {
    params.delete('open')
  }

  const query = params.toString()
  const newUrl = query ? `${window.location.pathname}?${query}` : window.location.pathname
  window.history.replaceState({}, '', newUrl)
}

function getStoredArray(key) {
  try {
    const raw = localStorage.getItem(key)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function setStoredArray(key, arr) {
  try {
    localStorage.setItem(key, JSON.stringify(arr))
  } catch {
  }
}

function cssEscape(value) {
  return String(value).replace(/"/g, '\\"')
}

/**
 * Auto-expand accordion sections based on current URL
 */
function autoExpandAccordions() {
  const currentPath = window.location.pathname

  const accordionMappings = {
    '/chapters/fractions/': 'fractions',
    '/chapters/percentages/': 'percentages',
    '/chapters/negative-numbers/': 'negative-numbers',
    '/chapters/fraction-conversions/': 'fraction-conversions'
  }

  Object.entries(accordionMappings).forEach(([pattern, accordionId]) => {
    if (currentPath.includes(pattern)) {
      const content = document.getElementById(`accordion-${accordionId}`)
      const toggle = document.querySelector(`[data-accordion="${accordionId}"]`)
      const icon = toggle?.querySelector('.accordion-icon')

      if (content) {
        content.classList.remove('hidden')
        icon?.classList.add('rotate-180')
      }
    }
  })
}

/**
 * Initialize theme toggle functionality
 */
function initThemeToggle() {
  const toggle = document.getElementById('theme-toggle')
  const html = document.documentElement

  if (!toggle) return

  // Check for saved theme preference or system preference
  const savedTheme = localStorage.getItem('theme')
  const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

  // Apply initial theme
  if (savedTheme === 'light') {
    html.classList.remove('dark')
  } else if (savedTheme === 'dark') {
    html.classList.add('dark')
  } else if (systemPrefersDark) {
    html.classList.add('dark')
  }

  // Update icon based on current theme
  updateThemeIcon()

  // Remove any existing listener to prevent duplicates
  const newToggle = toggle.cloneNode(true)
  toggle.parentNode.replaceChild(newToggle, toggle)

  newToggle.addEventListener('click', () => {
    const isDark = html.classList.contains('dark')

    if (isDark) {
      html.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    } else {
      html.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    }
    updateThemeIcon()
  })

  // Listen for system theme changes
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
    if (!localStorage.getItem('theme')) {
      if (e.matches) {
        html.classList.add('dark')
      } else {
        html.classList.remove('dark')
      }
      updateThemeIcon()
    }
  })
}

/**
 * Update theme toggle icon based on current theme
 */
function updateThemeIcon() {
  const html = document.documentElement
  const toggle = document.getElementById('theme-toggle')
  if (!toggle) return

  const isDark = html.classList.contains('dark')

  // Sun icon (shown in dark mode, click to go light)
  const sunIcon = `<svg class="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><path d="M12 3v1"/><path d="M12 20v1"/><path d="M3 12h1"/><path d="M20 12h1"/><path d="m18.364 5.636-.707.707"/><path d="m6.343 17.657-.707.707"/><path d="m5.636 5.636.707.707"/><path d="m17.657 17.657.707.707"/></svg>`

  // Moon icon (shown in light mode, click to go dark)
  const moonIcon = `<svg class="size-5" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/></svg>`

  toggle.innerHTML = isDark ? sunIcon : moonIcon
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLayout)
} else {
  initLayout()
}

// Re-initialize after Turbo navigation
document.addEventListener('turbo:load', initLayout)
document.addEventListener('turbo:render', initLayout)
